import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiBlob, ApiError } from "../lib/api";
import { useOrg } from "../lib/session";
import { useWords } from "../lib/workspace";
import { dateTime } from "../lib/format";
import type { Member } from "../lib/types";
import { Empty, ErrorText, Loading, PageHeader } from "../components/ui";

// Importing customers: upload a file, check how its columns are matched,
// choose how duplicates are handled, then watch it run. Imports can be undone
// for 7 days, and rows that couldn't be imported can be downloaded to fix.

const MAX_MB = 20;
const ACCEPT = ".xlsx,.csv,.tsv,.txt,.vcf,.vcard";

interface Options {
  duplicates: "skip" | "fill_empty" | "overwrite";
  country: string;
  lifecycle_stage: string;
  owner_member_id: string | null;
  consent: { email_marketing: boolean; whatsapp_service: boolean; whatsapp_marketing: boolean; confirmed: boolean };
}

interface Import {
  id: string;
  file_name: string;
  file_type: "csv" | "xlsx" | "vcf";
  sheets: string[];
  sheet: string | null;
  header_row: boolean;
  columns: string[];
  sample: string[][] | null;
  rows: number;
  truncated: boolean;
  mapping: Record<string, string>;
  options: Options;
  status: "mapping" | "queued" | "running" | "done" | "failed" | "cancelled" | "undoing" | "undone";
  processed: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  error: string | null;
  created_by: string | null;
  started_at: string | null;
  finished_at: string | null;
  can_undo: boolean;
  created_at: string;
}

interface View {
  import: Import;
  targets: { value: string; label: string; group: string }[];
  preview: { name: string; email: string; phone: string; stage: string; details: Record<string, string>; notes: number; problem: string }[];
  countries: { value: string; label: string }[];
}

const statusText: Record<Import["status"], string> = {
  mapping: "Not started", queued: "Waiting to start", running: "Importing", done: "Finished", failed: "Failed",
  cancelled: "Cancelled", undoing: "Undoing", undone: "Undone",
};

const errText = (err: unknown) => (err instanceof ApiError ? Object.values(err.fields ?? {})[0] ?? err.message : "Something went wrong.");

export function ImportList() {
  const { base, orgId } = useOrg();
  const navigate = useNavigate();
  const w = useWords();
  const list = useQuery({ queryKey: [orgId, "imports"], queryFn: () => api<{ imports: Import[] }>(`${base}/imports`) });
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(f: File | undefined) {
    if (!f) return;
    setError(null);
    if (f.size > MAX_MB << 20) {
      setError(`Files can be up to ${MAX_MB} MB. Split it into smaller files.`);
      return;
    }
    setBusy(true);
    const body = new FormData();
    body.append("file", f);
    try {
      const v = await api<View>(`${base}/imports`, { method: "POST", body });
      navigate(`/o/${orgId}/contacts/import/${v.import.id}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title={`Import ${w.lower("contact", true)}`} actions={<Link to={`/o/${orgId}/contacts`}>Back to {w.lower("contact", true)}</Link>} />
      <section className="card">
        <p className="muted">
          Bring in the {w.lower("contact", true)} you already have: an Excel sheet, a CSV export from another CRM, or contacts exported from a phone.
          You'll check how the columns are matched before anything is saved, and you can undo an import for 7 days.
        </p>
        <label
          className={`dropzone${dragging ? " over" : ""}${busy ? " busy" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); void send(e.dataTransfer.files[0]); }}
        >
          <input type="file" accept={ACCEPT} disabled={busy} onChange={(e) => { void send(e.target.files?.[0]); e.target.value = ""; }} />
          <b>{busy ? "Reading the file…" : "Choose a file or drop it here"}</b>
          <span className="muted small">Excel (.xlsx), CSV or phone contacts (.vcf) · up to {MAX_MB} MB · Arabic and English</span>
        </label>
        {error && <p className="error" role="alert">{error}</p>}
      </section>
      <section className="card">
        <h2>Past imports</h2>
        {list.isLoading ? <Loading /> : list.error ? <ErrorText error={list.error} /> : list.data!.imports.length === 0 ? <Empty>No imports yet.</Empty> : (
          <table className="table">
            <thead><tr><th>File</th><th>Status</th><th className="num">Added</th><th className="num">Updated</th><th className="num">Not imported</th><th>When</th></tr></thead>
            <tbody>
              {list.data!.imports.map((i) => (
                <tr key={i.id}>
                  <td><Link to={`/o/${orgId}/contacts/import/${i.id}`}>{i.file_name}</Link>{i.created_by && <div className="muted small">by {i.created_by}</div>}</td>
                  <td>{statusText[i.status]}</td>
                  <td className="num">{i.created}</td>
                  <td className="num">{i.updated}</td>
                  <td className="num">{i.failed}</td>
                  <td>{dateTime(i.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

export function ImportPage() {
  const { importId } = useParams();
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "import", importId];
  const running = (s?: Import["status"]) => s === "queued" || s === "running" || s === "undoing";
  const view = useQuery({
    queryKey: key,
    queryFn: () => api<View>(`${base}/imports/${importId}`),
    refetchInterval: (q) => (running(q.state.data?.import.status) ? 1500 : false),
  });
  useEffect(() => {
    if (view.data && !running(view.data.import.status)) qc.invalidateQueries({ queryKey: [orgId, "contacts"] });
  }, [view.data?.import.status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (view.isLoading) return <Loading />;
  if (view.error) return <ErrorText error={view.error} />;
  const v = view.data!;
  const setView = (nv: View) => qc.setQueryData(key, nv);
  return (
    <>
      <PageHeader title={v.import.file_name} actions={<Link to={`/o/${orgId}/contacts/import`}>All imports</Link>} />
      {v.import.status === "mapping" ? <Mapping v={v} onChange={setView} /> : <Progress v={v} onChange={setView} />}
    </>
  );
}

function Mapping({ v, onChange }: { v: View; onChange: (v: View) => void }) {
  const { base, orgId } = useOrg();
  const w = useWords();
  const navigate = useNavigate();
  const imp = v.import;
  const members = useQuery({ queryKey: [orgId, "members"], queryFn: () => api<{ members: Member[] }>(`${base}/members`) });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // Local copies so checkboxes and selects change instantly; the server's
  // answer then refreshes the preview.
  const [opts, setLocalOpts] = useState<Options>(imp.options);
  const [mapping, setMapping] = useState<Record<string, string>>(imp.mapping);
  useEffect(() => setMapping(imp.mapping), [imp.sheet, imp.header_row]); // re-suggested by the server

  // Only the newest answer is shown, so quick clicks can't be undone by a slower earlier reply.
  const seq = useRef(0);
  async function patch(json: Record<string, unknown>) {
    setError(null);
    const mine = ++seq.current;
    try {
      const nv = await api<View>(`${base}/imports/${imp.id}`, { method: "PATCH", json });
      if (mine === seq.current) onChange(nv);
    } catch (err) {
      if (mine === seq.current) setError(err);
    }
  }
  const setTarget = (col: number, target: string) => {
    const m = { ...mapping };
    // A field can take one column (notes can take several).
    if (target && target !== "note") for (const k of Object.keys(m)) if (m[k] === target) delete m[k];
    if (target) m[String(col)] = target; else delete m[String(col)];
    setMapping(m);
    void patch({ mapping: m });
  };
  const setOpts = (o: Partial<Options>) => {
    const next = { ...opts, ...o };
    setLocalOpts(next);
    void patch({ options: next });
  };
  const groups = Array.from(new Set(v.targets.map((t) => t.group)));
  const problems = v.preview.filter((p) => p.problem).length;
  const consent = opts.consent;
  const anyConsent = consent.email_marketing || consent.whatsapp_service || consent.whatsapp_marketing;

  return (
    <>
      <section className="card">
        <h2>1. Match the columns</h2>
        <p className="muted small">
          {imp.rows.toLocaleString()} rows found{imp.truncated && " (only the first part of the file is used: it's longer than your plan allows per import)"}.
          We matched what we could; check each column and pick "Don't import" for anything you don't need.
        </p>
        <div className="inline">
          {imp.file_type === "xlsx" && imp.sheets.length > 1 && (
            <label className="inline small">Sheet
              <select value={imp.sheet ?? ""} onChange={(e) => void patch({ sheet: e.target.value })}>
                {imp.sheets.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          )}
          {imp.file_type !== "vcf" && (
            <label className="inline small"><input type="checkbox" checked={imp.header_row} onChange={(e) => void patch({ header_row: e.target.checked })} /> The first row has column names</label>
          )}
        </div>
        <table className="table map-table">
          <thead><tr><th>Column in your file</th><th>Example values</th><th>Import into</th></tr></thead>
          <tbody>
            {imp.columns.map((c, i) => {
              const examples = (imp.sample ?? []).map((r) => r[i]).filter(Boolean).slice(0, 3);
              const target = mapping[String(i)] ?? "";
              return (
                <tr key={i} className={target ? "" : "muted"}>
                  <td><b>{c}</b></td>
                  <td className="small">{examples.length ? examples.map((x, j) => <div key={j} className="ellipsis">{x}</div>) : <span className="muted">empty</span>}</td>
                  <td>
                    <select aria-label={`Import ${c} into`} value={target} onChange={(e) => setTarget(i, e.target.value)}>
                      <option value="">Don't import</option>
                      {groups.map((g) => (
                        <optgroup key={g} label={g === "Contact" ? w("contact") : g}>
                          {v.targets.filter((t) => t.group === g).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="card">
        <h2>2. Check the first rows</h2>
        {v.preview.length === 0 ? <p className="muted small">Choose at least a name, email or phone column to see a preview.</p> : (
          <>
            {problems > 0 && <p className="notice small">{problems} of the first {v.preview.length} rows have a problem. They'll be skipped, and you can download them afterwards to fix.</p>}
            <table className="table small">
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Details</th><th></th></tr></thead>
              <tbody>
                {v.preview.map((p, i) => (
                  <tr key={i} className={p.problem ? "row-problem" : ""}>
                    <td>{p.name || "—"}</td>
                    <td>{p.email || "—"}</td>
                    <td>{p.phone || "—"}</td>
                    <td>{Object.entries(p.details).map(([k, val]) => <div key={k}><span className="muted">{k}:</span> {val}</div>)}{p.notes > 0 && <div className="muted">+ note</div>}</td>
                    <td>{p.problem && <span className="error">{p.problem}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      <section className="card">
        <h2>3. Choose how to import</h2>
        <fieldset className="field choices-col">
          <legend>When someone is already in SVENEX (same email or phone)</legend>
          {([["skip", "Skip them: keep what's in SVENEX"], ["fill_empty", "Fill in only missing details"], ["overwrite", "Update them with the file's details"]] as const).map(([val, label]) => (
            <label key={val} className="inline small"><input type="radio" name="dups" checked={opts.duplicates === val} onChange={() => setOpts({ duplicates: val })} /> {label}</label>
          ))}
        </fieldset>
        <div className="row">
          <label className="field"><span>Phone numbers without a country code are from</span>
            <select value={opts.country} onChange={(e) => setOpts({ country: e.target.value })}>
              {v.countries.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="field"><span>Stage for new {w.lower("contact", true)}</span>
            <select value={opts.lifecycle_stage} onChange={(e) => setOpts({ lifecycle_stage: e.target.value })}>
              {["lead", "qualified", "customer", "inactive"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="field"><span>Owner of new {w.lower("contact", true)}</span>
            <select value={opts.owner_member_id ?? ""} onChange={(e) => setOpts({ owner_member_id: e.target.value || null })}>
              <option value="">Me</option>
              {members.data?.members.filter((m) => m.status === "active" && m.crm_role).map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
          </label>
        </div>
        <details className="consent-box" open={anyConsent}>
          <summary>Record marketing permission (optional)</summary>
          <p className="muted small">
            Only tick these if every person in this file agreed to it, for example on a sign-up form. SVENEX records that you confirmed it.
            Without permission, people can still get replies and service messages, but not offers.
          </p>
          <label className="inline small"><input type="checkbox" checked={consent.email_marketing} onChange={(e) => setOpts({ consent: { ...consent, email_marketing: e.target.checked, confirmed: false } })} /> They agreed to marketing emails</label>
          <label className="inline small"><input type="checkbox" checked={consent.whatsapp_service} onChange={(e) => setOpts({ consent: { ...consent, whatsapp_service: e.target.checked, confirmed: false } })} /> They agreed to be contacted on WhatsApp</label>
          <label className="inline small"><input type="checkbox" checked={consent.whatsapp_marketing} onChange={(e) => setOpts({ consent: { ...consent, whatsapp_marketing: e.target.checked, confirmed: false } })} /> They agreed to WhatsApp offers</label>
          {anyConsent && (
            <label className="inline small confirm"><input type="checkbox" checked={consent.confirmed} onChange={(e) => setOpts({ consent: { ...consent, confirmed: e.target.checked } })} /> I confirm everyone in this file gave this permission, and we can show it if asked.</label>
          )}
        </details>
        <ErrorText error={error} />
        <div className="inline" style={{ marginTop: "1rem" }}>
          <button disabled={busy || (anyConsent && !consent.confirmed) || Object.keys(mapping).length === 0} onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              onChange(await api<View>(`${base}/imports/${imp.id}/start`, { method: "POST" }));
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}>{busy ? "Starting…" : `Import ${imp.rows.toLocaleString()} rows`}</button>
          <button className="secondary" onClick={async () => {
            await api(`${base}/imports/${imp.id}/cancel`, { method: "POST" });
            navigate(`/o/${orgId}/contacts/import`);
          }}>Cancel</button>
        </div>
      </section>
    </>
  );
}

function Progress({ v, onChange }: { v: View; onChange: (v: View) => void }) {
  const { base, orgId } = useOrg();
  const w = useWords();
  const imp = v.import;
  const [error, setError] = useState<unknown>(null);
  const pct = imp.rows ? Math.min(100, Math.round((imp.processed / imp.rows) * 100)) : 0;
  const active = imp.status === "queued" || imp.status === "running";

  async function download() {
    setError(null);
    try {
      const blob = await apiBlob(`${base}/imports/${imp.id}/problems.csv`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = imp.file_name.replace(/\.[^.]+$/, "") + "-problems.csv";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <section className="card">
      <h2>{statusText[imp.status]}</h2>
      {active && (
        <>
          <div className="progress-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${pct}%` }} /></div>
          <p className="muted small">{imp.processed.toLocaleString()} of {imp.rows.toLocaleString()} rows. You can leave this page; the import keeps going.</p>
        </>
      )}
      {imp.status === "undoing" && <p className="muted small">Removing what this import added and restoring what it changed…</p>}
      {imp.status === "failed" && <p className="error">{imp.error}</p>}
      <dl className="details stats-list">
        <dt>Added</dt><dd>{imp.created.toLocaleString()} new {w.lower("contact", true)}</dd>
        <dt>Updated</dt><dd>{imp.updated.toLocaleString()}</dd>
        <dt>Skipped</dt><dd>{imp.skipped.toLocaleString()} <span className="muted small">(already in SVENEX, or no name, email or phone)</span></dd>
        <dt>Not imported</dt><dd>{imp.failed.toLocaleString()}{imp.failed > 0 && <> · <button className="link" onClick={download}>Download these rows with the reasons</button></>}</dd>
        {imp.finished_at && <><dt>Finished</dt><dd>{dateTime(imp.finished_at)}</dd></>}
      </dl>
      <ErrorText error={error} />
      <div className="inline" style={{ marginTop: "1rem" }}>
        {imp.status === "done" && <Link className="button-link" to={`/o/${orgId}/contacts`}>See {w.lower("contact", true)}</Link>}
        {active && <button className="secondary" onClick={async () => onChange(await api<View>(`${base}/imports/${imp.id}/cancel`, { method: "POST" }))}>Stop the import</button>}
        {imp.can_undo && (
          <button className="secondary" onClick={async () => {
            if (!confirm(`Undo this import? The ${imp.created} ${w.lower("contact", true)} it added will be archived, and details it changed on ${imp.updated} existing ones will be put back.`)) return;
            try {
              onChange(await api<View>(`${base}/imports/${imp.id}/undo`, { method: "POST" }));
            } catch (err) {
              setError(err);
            }
          }}>Undo import</button>
        )}
      </div>
      {imp.status === "done" && !imp.can_undo && imp.created + imp.updated > 0 && <p className="muted small">Imports can be undone for 7 days.</p>}
    </section>
  );
}
